import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { matchChallengeKeys, useChallengePositionList } from '@repository/match';
import PositionSelect from '../positionSelect';
import MatchingChallengeVO from '@domain/vo/challenge/MatchingChallengeVO';
import { MatchingChallengePositionVO } from '@domain/vo/challenge/MatchingChallengePositionListVO';
import { JobApplicationProcessType } from '@domain/constant/jobApplicationProcessType';
import { queryClient } from '@utils/queryClient';
import { V2Route } from '@utils/routeUtil';
import NumberFormat from '@utils/number/format';
import classnames from 'classnames/bind';
import styles from './joinPositionList.module.scss';

const cx = classnames.bind(styles);

interface JoinPositionListTitleProps {
  challengeData: MatchingChallengeVO;
  isChallengePositions: boolean;
  selectedCompanySns: number[];
  setSelectedCompanySns: (state: number[]) => void;
}

const JoinPositionListTitle = ({
  challengeData,
  isChallengePositions,
  selectedCompanySns,
  setSelectedCompanySns,
}: JoinPositionListTitleProps) => {
  const isChallengeReady = challengeData.challengeStatus === 'READY';

  const getBannerText = () => {
    const isInfinity = challengeData.applicableSize === null;

    if (isInfinity) {
      return '해당 채용이벤트의 모든 포지션에 지원이 가능합니다.';
    }

    return `해당 채용이벤트 내에서 ${challengeData.applicableSize}개의 포지션에 지원이 가능합니다.`;
  };

  const bannerText = getBannerText();

  return (
    <>
      <div className={cx('titleArea')}>
        <div className={cx('titleWrap')}>
          <strong className={cx('title')}>참여 포지션</strong>
        </div>
        <div className={cx('selectArea', { hidden: isChallengeReady })}>
          <JoinPositionListSelect
            selectedCompanySns={selectedCompanySns}
            setSelectedCompanySns={setSelectedCompanySns}
          />
        </div>
      </div>
      <div className={cx('positionInfoBanner', { hidden: isChallengeReady })}>{bannerText}</div>
    </>
  );
};

interface JoinPositionListSelectProps {
  selectedCompanySns: number[];
  setSelectedCompanySns: (state: number[]) => void;
}

const JoinPositionListSelect = ({ selectedCompanySns, setSelectedCompanySns }: JoinPositionListSelectProps) => {
  const onClick = () => setSelectedCompanySns([]);

  return (
    <div className={cx('selectWrap')}>
      <PositionSelect selectedCompanySns={selectedCompanySns} setSelectedCompanySns={setSelectedCompanySns} />
      <a className={cx('btnReset')} role="button" onClick={onClick}>
        필터초기화
      </a>
    </div>
  );
};

interface JoinPositionStatusCardProps {
  jobApplicationProcessType: JobApplicationProcessType | null;
}

const JoinPositionStatusCard = ({ jobApplicationProcessType }: JoinPositionStatusCardProps) => {
  if (!jobApplicationProcessType) return null;
  switch (jobApplicationProcessType) {
    case 'WRITING_RESUME':
      return (
        <div className={cx('applyStatus', 'writing')}>
          지원서
          <br />
          작성중
        </div>
      );
    default:
      return (
        <div className={cx('applyStatus')}>
          지원 <br />
          완료
        </div>
      );
  }
};

const joinPositionEl = (challengePosition: MatchingChallengePositionVO) => {
  const isCompanyNameEllipsis = challengePosition.companyName.length > 30;
  return (
    <Link
      key={challengePosition.positionSn}
      href={V2Route.getPositionJDPage(challengePosition.positionSn)}
      legacyBehavior
    >
      <a className={cx('joinPositionWrap')} role="button">
        <div className={cx('positionInfoWrap')}>
          <strong className={cx('positionTitle')}>{challengePosition.positionName}</strong>
          <div className={cx('positionInfoList')}>
            <span className={cx('positionInfo')}>{`${challengePosition.companyName.slice(0, 30)}...`}</span>
            {isCompanyNameEllipsis && (
              <div className={cx('tooltip')}>
                <div className={cx('tooltipBox')}>{challengePosition.companyName}</div>
              </div>
            )}
            <span className={cx('positionInfo')}>{challengePosition.location}</span>
            <span className={cx('positionInfo')}>{challengePosition.jobTitleName}</span>
            <span className={cx('positionInfo')}>{challengePosition.recruitmentType}</span>
            {challengePosition.minSalary && (
              <span className={cx('positionInfo')}>{`연봉 ${NumberFormat.formatDecimal(
                challengePosition.minSalary,
              )}만원 이상`}</span>
            )}
          </div>
        </div>
        <JoinPositionStatusCard jobApplicationProcessType={challengePosition.jobApplicationProcessType} />
      </a>
    </Link>
  );
};

const EmptyJoinPositionList = () => {
  return (
    <div className={cx('emptyPositionList')}>
      <div className={cx('positionListImage')} />
      <strong className={cx('positionListText')}>
        어마어마한 포지션들이
        <br />곧 공개됩니다.
      </strong>
    </div>
  );
};

const JoinPositionList = () => {
  const { query } = useRouter();
  const sn = Number(query.sn);
  const [selectedCompanySns, setSelectedCompanySns] = useState<number[]>([]);
  const challengeData = queryClient.getQueryData<MatchingChallengeVO>([...matchChallengeKeys, sn]);
  const { data, isLoading } = useChallengePositionList(sn, selectedCompanySns);

  if (!challengeData || !data || isLoading) return null;

  const isChallengeReady = challengeData.challengeStatus === 'READY';
  const joinPositions = data.challengePositions.map((challengePosition) => joinPositionEl(challengePosition));
  const isChallengePositions = data.challengePositions.length !== 0;

  return (
    <>
      <JoinPositionListTitle
        challengeData={challengeData}
        isChallengePositions={isChallengePositions}
        selectedCompanySns={selectedCompanySns}
        setSelectedCompanySns={setSelectedCompanySns}
      />
      {!isChallengeReady ? joinPositions : <EmptyJoinPositionList />}
    </>
  );
};

export default JoinPositionList;
